import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { useSearchShortcut } from "@hooks/admin/useSearchShortcut";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";

import { AdminAppBar } from "./AdminAppBar";
import { AdminNavigationDrawer } from "./AdminNavigationDrawer";
import { AdminSidebar } from "./AdminSidebar";
import { AdminTopBar } from "./AdminTopBar";
import { LegacyAdminNotice } from "./LegacyAdminNotice";
import { narrowWindowWidth, phoneContentPadding, phoneContentPaddingBottom } from "./layout";

const { color, typeScale } = adminTokens;
// The design's content area: 28px above, 32px at each side, 40px below; on a phone, the
// paddings of layout.ts (SHELL-R22, SHELL-R46).
const contentPaddingTop = 28;
const contentPaddingSides = 32;
const contentPaddingBottom = 40;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Frame = styled.div`
  display: flex;
  flex: 1;

  @media (max-width: ${narrowWindowWidth - 1}px) {
    flex-direction: column;
  }
`;

const Column = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

const phoneContentStyle = css`
  padding: ${phoneContentPadding}px ${phoneContentPadding}px ${phoneContentPaddingBottom}px;
`;

const Content = styled.main<{ $isPadded: boolean; $isPhone: boolean }>`
  flex: 1;
  min-width: 0;
  background-color: ${color.canvas};
  padding: ${({ $isPadded }) =>
    $isPadded ? `${contentPaddingTop}px ${contentPaddingSides}px ${contentPaddingBottom}px` : "0"};
  ${({ $isPadded, $isPhone }) => $isPadded && $isPhone && phoneContentStyle}

  /* Focus lands here only from code (SHELL-R28, SHELL-R44); the screen is not a control. */
  &:focus {
    outline: none;
  }
`;

const PhoneSearchArea = styled.div`
  flex: 1;
  background-color: ${color.canvas};
`;

// The design's line below the open search bar (SHELL-R43).
const SearchHint = styled.p`
  margin: 0;
  padding: ${phoneContentPadding}px;
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  line-height: ${typeScale.body.lineHeight};
`;

type FocusAfterSearch = "search" | "screen";

// The state of the phone's app bar: whether the drawer or the search bar is open,
// and which control takes keyboard focus once the search bar closes (SHELL-R44,
// SHELL-R45). Both close when the window leaves the phone layout, so they do not
// reopen unasked when it comes back.
const usePhoneShell = (isPhone: boolean, contentRef: RefObject<HTMLElement>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const focusAfterSearchRef = useRef<FocusAfterSearch | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isPhone) return;

    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [isPhone]);

  useEffect(() => {
    if (isSearchOpen) return;

    const focusAfterSearch = focusAfterSearchRef.current;
    focusAfterSearchRef.current = null;
    if (focusAfterSearch === "search") searchButtonRef.current?.focus();
    if (focusAfterSearch === "screen") contentRef.current?.focus();
  }, [isSearchOpen, contentRef]);

  const closeSearch = (focus: FocusAfterSearch) => {
    focusAfterSearchRef.current = focus;
    setIsSearchOpen(false);
  };

  return {
    isMenuOpen,
    isSearchOpen,
    menuButtonRef,
    searchButtonRef,
    openMenu: () => setIsMenuOpen(true),
    closeMenu: () => setIsMenuOpen(false),
    openSearch: () => setIsSearchOpen(true),
    cancelSearch: () => closeSearch("search"),
    finishSearch: () => closeSearch("screen"),
  };
};

interface AdminShellProps {
  children: ReactNode;
  // "screen": the shell pads the screen (SHELL-R22); "none": the screen, such as the
  // UI kit gallery, pads its own page.
  padding?: "screen" | "none";
}

// The frame around every screen of the rebuilt admin: the sidebar at the left, the
// top bar above the screen, and the screen on the canvas (SHELL-R1), with the
// legacy admin notice above them all (SHELL-R24). On a phone an app bar with a
// navigation drawer and a search bar takes the place of the sidebar and the top bar
// (SHELL-R29). The screen keeps its place in the tree, so crossing the phone width
// does not reset it.
export const AdminShell = ({ children, padding = "screen" }: AdminShellProps) => {
  const isPhone = useIsPhoneLayout();
  const contentRef = useRef<HTMLElement>(null);
  const phone = usePhoneShell(isPhone, contentRef);
  const isSearching = isPhone && phone.isSearchOpen;

  const topBarSearchRef = useRef<HTMLInputElement>(null);

  // "/" puts the focus in the search; on a phone it opens the search bar, which takes the
  // focus. The open drawer is a dialog, so the shortcut leaves it alone (SHELL-R49, SHELL-R50,
  // SHELL-R53).
  useSearchShortcut(() => {
    if (isPhone) {
      phone.openSearch();
      return;
    }
    topBarSearchRef.current?.focus();
  });

  // Dismissing the notice removes the focused button, so the screen takes focus (SHELL-R28).
  const focusContent = () => contentRef.current?.focus();

  return (
    <Page>
      <LegacyAdminNotice onDismiss={focusContent} />
      <Frame>
        {!isPhone && <AdminSidebar />}
        <Column>
          {isPhone ? (
            <AdminAppBar
              isMenuOpen={phone.isMenuOpen}
              isSearchOpen={phone.isSearchOpen}
              menuButtonRef={phone.menuButtonRef}
              searchButtonRef={phone.searchButtonRef}
              onOpenMenu={phone.openMenu}
              onOpenSearch={phone.openSearch}
              onCloseSearch={phone.cancelSearch}
              onSearched={phone.finishSearch}
            />
          ) : (
            <AdminTopBar ref={topBarSearchRef} />
          )}
          {isSearching && (
            <PhoneSearchArea>
              <SearchHint>
                Find a User by email, or an Order by Order ID or Case ID. Press return to search.
              </SearchHint>
            </PhoneSearchArea>
          )}
          <Content
            ref={contentRef}
            tabIndex={-1}
            hidden={isSearching}
            $isPadded={padding === "screen"}
            $isPhone={isPhone}
          >
            {children}
          </Content>
        </Column>
      </Frame>
      {isPhone && <AdminNavigationDrawer isOpen={phone.isMenuOpen} onClose={phone.closeMenu} />}
    </Page>
  );
};
